import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Music, Award, Calendar, Mail } from "lucide-react";
import { LeaderboardBadge } from "@/components/LeaderboardBadge";
import { useLeaderboardPosition } from "@/hooks/useLeaderboardPosition";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";
import { UserBadge } from "@/components/UserBadge";

// Helper component to show leaderboard badge for a user
function UserLeaderboardBadge({ userId }) {
  const { data: position } = useLeaderboardPosition(userId);
  return position ? <LeaderboardBadge position={position} /> : null;
}

export default function PublicProfile() {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = new URLSearchParams(location.search).get("id");
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    if (session) {
      const { data: profile } = await digitalOcean
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      setCurrentUser(profile);
    }
  };

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["public-profile", userId],
    queryFn: async () => {
      const { data, error } = await digitalOcean
        .from("public_profiles")
        .select("*")
        .eq("id", userId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const { data: tracks } = useQuery({
    queryKey: ["user-tracks", userId],
    queryFn: async () => {
      const { data, error } = await digitalOcean
        .from("tracks")
        .select("*")
        .eq("artist_id", userId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const { data: reviewCount } = useQuery({
    queryKey: ["user-review-count", userId],
    queryFn: async () => {
      const { count, error } = await digitalOcean
        .from("feedback")
        .select("*", { count: "exact", head: true })
        .eq("reviewer_id", userId);
      if (error) throw error;
      return count || 0;
    },
    enabled: !!userId,
  });

  // Fetch user badges - only show if user is not anonymous
  // Uses profile.is_anonymous from public_profiles view (safe for public access)
  const { data: userBadges = [] } = useQuery({
    queryKey: ["user-badges", userId, profile?.is_anonymous],
    queryFn: async () => {
      if (!userId) return [];
      
      // Only fetch badges if user is not anonymous (using data from public_profiles view)
      if (profile?.is_anonymous) return [];
      
      const { data, error } = await digitalOcean
        .from("user_badges")
        .select("badge_name")
        .eq("user_id", userId)
        .order("earned_at", { ascending: false });
      
      if (error) {
        console.error("Error fetching badges:", error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!userId && !!profile,
  });

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl text-foreground mb-4">Profile Not Found</h1>
          <Button onClick={() => navigate("/Discover")} tooltip="Return to the discover page">Go to Discover</Button>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === profile.id;

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-card p-8 rounded-lg border border-border mb-8">
          <div className="flex items-start gap-8">
            <img
              src={profile.avatar_url || "/placeholder.svg"}
              alt={profile.username}
              className="w-32 h-32 rounded-full object-cover"
            />
            
            <div className="flex-1">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-4xl font-bold text-card-foreground mb-2 flex items-center gap-2">
                    {profile.artist_name || profile.username}
                    <UserLeaderboardBadge userId={profile.id} />
                  </h1>
                  <p className="text-muted-foreground">@{profile.username}</p>
                </div>
                
                {isOwnProfile ? (
                  <Button onClick={() => navigate("/Profile")} tooltip="Edit your profile settings">Edit Profile</Button>
                ) : (
                  <Button onClick={() => navigate(`/Inbox`)} tooltip="Send a message to this user">
                    <Mail className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                )}
              </div>

              {profile.bio && (
                <p className="text-card-foreground mb-4">{profile.bio}</p>
              )}

              <div className="flex gap-6 mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Joined {new Date(profile.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {profile.points || 0} points
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{tracks?.length || 0}</div>
                  <div className="text-sm text-muted-foreground">Tracks</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{reviewCount || 0}</div>
                  <div className="text-sm text-muted-foreground">Reviews</div>
                </div>
              </div>

              {/* User Badges Section - only show if user is not anonymous */}
              {userBadges.length > 0 && (
                <div className="pt-4 border-t border-border mt-4">
                  <h3 className="text-lg font-semibold text-foreground mb-3">Badges</h3>
                  <div className="flex flex-wrap gap-3">
                    {userBadges.map((badge) => (
                      <UserBadge key={badge.badge_name} badgeName={badge.badge_name} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {tracks && tracks.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Music className="w-6 h-6" />
              Tracks
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tracks.map((track) => (
                <TooltipProvider key={track.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="bg-card p-4 rounded-lg border border-border hover:border-primary transition-colors cursor-pointer"
                        onClick={() => navigate(`/TrackDetails?id=${track.id}`)}
                      >
                  <img
                    src={track.cover_image_url || getDefaultCoverImage(track.id)}
                    alt={track.title}
                    className="w-full aspect-square object-cover rounded mb-3"
                  />
                  <h3 className="font-semibold text-card-foreground truncate mb-1">
                    {track.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1.5">
                    {track.artist_name}
                    {track.artist_id && <UserLeaderboardBadge userId={track.artist_id} />}
                  </p>
                  
                  <div className="flex gap-3 text-sm">
                    <span className="text-green-500">❤️ {track.praise_count}</span>
                    <span className="text-muted-foreground">👂 {track.total_listens}</span>
                  </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View track details and feedback</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
